import React, { useEffect, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Multi-select tag picker: choose from the central tag list (chips) and type a new tag on the fly.
// value: string[]; onChange: (string[]) => void
export default function TagMultiSelect({ value = [], onChange, testid = 'tag-select', allowCreate = true, area = '' }) {
  const { getTagsMeta, getTagsUsed } = useAuth();
  const [used, setUsed] = useState({});
  const [all, setAll] = useState([]);
  const [colors, setColors] = useState({});
  const [input, setInput] = useState('');

  useEffect(() => {
    getTagsMeta().then((d) => { setAll(Array.isArray(d.names) ? d.names : []); setColors(d.colors || {}); }).catch(() => { setAll([]); setColors({}); });
    if (area) getTagsUsed(area).then((d) => setUsed(Object.fromEntries((d.used || []).map((u) => [u.name, u.count])))).catch(() => setUsed({}));
  }, [getTagsMeta, getTagsUsed, area]);

  const colorOf = (t) => colors[String(t).toLowerCase()] || 'var(--maroon)';
  const isLight = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return false;
    const n = parseInt(m[1], 16);
    return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) > 200;
  };
  const has = (t) => value.some((v) => v.toLowerCase() === t.toLowerCase());
  const toggle = (t) => (has(t) ? onChange(value.filter((v) => v.toLowerCase() !== t.toLowerCase())) : onChange([...value, t]));
  const addNew = () => {
    const t = input.trim();
    if (t && !has(t)) onChange([...value, t]);
    setInput('');
  };

  // Tags already used in this area come first (most used first), then the rest of the central registry.
  const suggestions = [...all.filter((t) => !has(t))].sort((a, b) => ((used[b] || 0) - (used[a] || 0)) || a.localeCompare(b));
  const usedCount = suggestions.filter((t) => used[t]).length;

  return (
    <div data-testid={testid}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((t) => (
            <span key={t} data-testid={`${testid}-chip-${t}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-title"
              style={{ background: colorOf(t), color: isLight(colorOf(t)) ? '#3a2c06' : '#fff' }}>
              #{t}
              <button type="button" onClick={() => toggle(t)} data-testid={`${testid}-remove-${t}`} className="hover:opacity-80"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {allowCreate && (
        <div className="flex gap-2 mb-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }}
            placeholder="Type a tag and press Enter…"
            data-testid={`${testid}-input`}
            className="flex-1 px-2.5 py-1.5 rounded border text-sm"
            style={{ borderColor: 'rgba(0,0,0,0.2)' }}
          />
          <button type="button" onClick={addNew} data-testid={`${testid}-add`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-sm font-title"
            style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
            <Plus size={14} /> Add
          </button>
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((t) => (
            <button key={t} type="button" onClick={() => toggle(t)} data-testid={`${testid}-option-${t}`}
              className="px-2 py-0.5 rounded-full text-xs"
              style={{ background: 'transparent', color: isLight(colorOf(t)) ? '#3a2c06' : colorOf(t), border: `1px solid ${isLight(colorOf(t)) ? 'rgba(0,0,0,0.35)' : colorOf(t)}` }}>
              #{t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
